import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import DesignPreview from '~/components/DesignPreview';
import ProductImage from '~/components/Product/ProductImage';
import RuledHeader from '~/shared/components/RuledHeader/index';
import Spinner from '~/components/SimpleSpinner';

import {getColor, getViewImage} from '~/utils/catalog';
import {sizeRange} from '~/utils/sizeHelpers';
import {Link, Card, CardContent, Typography} from '@customink/pigment-react';

import './ComplementCard.scss';

function renderProductImage({
  style,
  color,
  icons,
  selected,
  anchorStyle,
  getProductViews,
  index,
  isAlternatives
}) {
  const view = getProductViews(anchorStyle)?.find(v => v?.name === 'front');
  const {uri} = getViewImage(color, 'front');
  if (view) {
    return (
      <div key="preview" className="ndx-ComplementCard-productImageContainer">
        <DesignPreview
          className="ndx-ComplementCard-preview"
          view={view}
          colorId={color.id}
          icons={icons}
          style={style}
          viewIdentifier="front"
        />
        {isAlternatives && index === 0 && renderRecommendedProduct()}
      </div>
    );
  }

  return (
    <span key="preview" className="ndx-ComplementCard-productImageContainer">
      <ProductImage
        className="ndx-ComplementCard-preview"
        imagePath={uri}
        productId={color.id}
        showLoader={false}
        styleId={style.id}
      />
      {renderSelectedProduct(selected)}
    </span>
  );
}

function renderRecommendedProduct() {
  return <RuledHeader>Recommended</RuledHeader>;
}

function renderSelectedProduct(selected) {
  return selected ? <RuledHeader>Current Product</RuledHeader> : null;
}

function renderSwitchStyle() {
  return (
    <Link className="ndx-ComplementCard-details--alternatives-cta-switchStyle">
      Switch To This Style
    </Link>
  );
}

function renderCurrentSelection() {
  return (
    <div className="ndx-ComplementCard-details--alternatives-cta-currentSelection">
      {'Current selection'.toUpperCase()}
    </div>
  );
}

function renderComplementDetails(style, detailed, selected) {
  return (
    <>
      <CardContent className="ndx-ComplementCard-details">
        <Typography
          variant="uiTextSmall"
          className="ndx-ComplementCard-details-styleName">
          {style.name}
        </Typography>
        {detailed && (
          <Typography variant="uiTextTiny">
            Sizes: {sizeRange(style.sizes)}
            <br />
            {style.colors.filter(c => c.status === 'active').length} colors
          </Typography>
        )}
      </CardContent>
      <CardContent className="ndx-ComplementCard-actions">
        <Link button>
          {selected ? 'Add another color' : 'Add this product'}
        </Link>
      </CardContent>
    </>
  );
}

function renderAlternativeDetails(style, baseStyle) {
  return (
    <>
      <CardContent className="ndx-ComplementCard-details">
        <div className="ndx-ComplementCard-details--alternatives-styleName">
          {style.name}
        </div>
        <li>{style.material.split(/;|\* | \(/)[1]}</li>
      </CardContent>
      <CardContent className="ndx-ComplementCard-actions">
        {baseStyle && style.id !== baseStyle.id
          ? renderSwitchStyle()
          : renderCurrentSelection()}
      </CardContent>
    </>
  );
}

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
function ComplementCard({
  colorId,
  detailed,
  icons,
  style,
  selected,
  onClick,
  anchorStyle,
  getProductViews,
  index,
  isAlternatives,
  baseStyle
}) {
  const color = getColor(style, colorId);
  const isLoaded = !!(style && color);

  const className = classnames(
    'ndx-ComplementCard',
    'ndx-ComplementCard--small',
    {
      'ndx-ComplementCard--loading': !isLoaded,
      alternatives: isAlternatives
    }
  );

  return (
    <Card
      size="small"
      className={className}
      data-testid="complement-card"
      data-color-id={colorId}
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default'
      }}>
      {isLoaded ? (
        <>
          {renderProductImage({
            style,
            color,
            icons,
            selected,
            anchorStyle,
            getProductViews,
            isAlternatives,
            index
          })}
          {isAlternatives
            ? renderAlternativeDetails(style, baseStyle)
            : renderComplementDetails(style, detailed, selected)}
        </>
      ) : (
        <Spinner />
      )}
    </Card>
  );
}

const propTypes = {
  anchorStyle: PropTypes.object,
  baseStyle: PropTypes.object,
  colorId: PropTypes.string,
  detailed: PropTypes.bool,
  getProductViews: PropTypes.func,
  icons: PropTypes.array,
  index: PropTypes.number,
  isAlternatives: PropTypes.bool,
  selected: PropTypes.bool,
  style: PropTypes.object,
  onClick: PropTypes.func
};

ComplementCard.propTypes = propTypes;

export default ComplementCard;
